/**
 * POS Shift Start — full-screen "Start your shift" screen.
 *
 * The cashier must:
 *   1. Pick an event (or start a new one — owner only)
 *
 * The cashier identity is LOCKED to the current logged-in user. The
 * previous version of this screen let a logged-in user pick any other
 * active user (a "switch cashier" feature) — that has been removed so
 * no one can charge transactions under another person's name.
 *
 * On confirm, `usePosShiftStore.startShift(...)` is called, which
 * gates the actual POS UI on the parent route.
 *
 * Visual design: kiosk-friendly. Large tap targets, generous spacing,
 * clear hierarchy. The "Back to admin" button is a small text link at
 * the corner so the admin can escape the kiosk mode.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarRange,
  ChevronRight,
  LogIn,
  Plus,
  User,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Chip } from '@/components/ui/Chip';
import { useAuth } from '@/hooks/useAuth';
import { useActiveEvents, useEvent, useCreateEvent } from '@/hooks/useEvents';
import { usePosShiftStore } from '@/store/posShift';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDateTime, formatRelative } from '@/lib/format';
import type { AppEvent } from '@/types/domain';

export default function POSShiftStart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: activeEvents } = useActiveEvents();
  const createEvent = useCreateEvent();
  const startShift = usePosShiftStore((s) => s.startShift);
  const endedNotice = usePosShiftStore((s) => s.endedNotice);
  const clearNotice = usePosShiftStore((s) => s.clearNotice);

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [showNewEventForm, setShowNewEventForm] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { can } = usePermissions();
  const isAdmin = can('events', 'create');
  const { data: selectedEventData } = useEvent(selectedEventId);

  // The cashier identity is fixed to whoever's logged in. We resolve
  // display name with a sane fallback so a freshly-provisioned account
  // (displayName may be the raw uid from the auth flow) still shows
  // something readable on the success screen.
  const cashierDisplay = useMemo(() => {
    const name = user?.displayName?.trim();
    if (name) return name;
    if (user?.email) return user.email.split('@')[0];
    return 'Kasir';
  }, [user?.displayName, user?.email]);

  const handleStart = () => {
    if (!user) {
      setError('Tidak ada user login.');
      return;
    }
    if (!selectedEventId) {
      setError('Pilih event dulu.');
      return;
    }
    const event = selectedEventData;
    if (!event) {
      setError('Event tidak ditemukan.');
      return;
    }
    setError(null);
    startShift({
      eventId: event.id,
      eventName: event.name,
      eventCode: event.code,
      // SECURITY: always use the authenticated user's uid. This used
      // to be a free-form picker; it now can never be someone else.
      cashierId: user.uid,
      cashierName: cashierDisplay,
    });
  };

  const handleCreateAndStart = async () => {
    if (!user || !newEventName.trim()) {
      setError('Nama event wajib diisi.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const newId = await createEvent.mutateAsync({
        name: newEventName.trim(),
        createdBy: user.uid,
      });
      startShift({
        eventId: newId,
        eventName: newEventName.trim(),
        eventCode: '(baru)',
        cashierId: user.uid,
        cashierName: cashierDisplay,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      {/* Top bar — minimal, with "Back to admin" escape hatch */}
      <header className="px-4 sm:px-8 py-3 border-b border-line bg-surface flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-primary/10 text-primary flex items-center justify-center">
            <LogIn size={16} />
          </div>
          <p className="text-subhead font-display font-semibold text-ink">Mulai shift</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="text-caption text-ink-subtle hover:text-ink transition-colors"
        >
          ← Kembali ke admin
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-3xl space-y-6">
          {endedNotice && (
            <div role="status" className="p-3 rounded border border-warning/30 bg-warning/5 text-caption text-ink flex items-start justify-between gap-3">
              <span>{endedNotice}</span>
              <button type="button" onClick={clearNotice} className="text-ink-subtle hover:text-ink shrink-0" aria-label="Tutup">✕</button>
            </div>
          )}
          {error && (
            <div className="p-3 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
              {error}
            </div>
          )}

          <div className="text-center mb-6">
            {/* `display` is 72px — sized for a marketing page, not for a name
                of unknown length on a POS tablet. "Halo, Sidiana Lynna 👋" at
                72px is ~780px of text, wider than the 640px shift card, and it
                broke across three lines. `subhead` reads as a greeting rather
                than a billboard and holds any name we can be handed. */}
            <h1 className="text-subhead font-display font-semibold text-ink break-words">
              Halo, {cashierDisplay} 👋
            </h1>
            <p className="text-body text-ink-subtle mt-2">
              Pilih event untuk mulai shift POS. Transaksi akan tercatat atas nama kamu.
            </p>
          </div>

          {/* Cashier identity card — fixed to the current user */}
          <Card padding="md" className="border-primary/30 bg-primary/5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <User size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-overline uppercase text-ink-subtle font-medium leading-none">
                  Masuk sebagai
                </p>
                <p className="text-body font-display font-semibold text-ink truncate mt-1">
                  {cashierDisplay}
                </p>
                <p className="text-caption text-ink-subtle truncate">
                  {user?.email}
                </p>
              </div>
              <Chip variant="info" className="shrink-0">
                <ShieldCheck size={11} />
                Locked
              </Chip>
            </div>
            <p className="text-caption text-ink-subtle mt-3 leading-relaxed">
              Untuk keamanan, identitas kasir dikunci ke akun yang sedang login.
              Tidak ada pilihan untuk charge atas nama orang lain.
            </p>
          </Card>

          {/* Event picker */}
          <Card padding="md">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded bg-primary/10 text-primary flex items-center justify-center">
                <CalendarRange size={14} />
              </div>
              <h2 className="text-subhead font-display font-semibold text-ink">Pilih event</h2>
            </div>

            {!showNewEventForm ? (
              <>
                <div className="grid gap-2 sm:grid-cols-2">
                  {(activeEvents ?? []).length === 0 && (
                    <div className="col-span-full p-4 text-center text-caption text-ink-subtle border border-dashed border-line rounded">
                      Belum ada event aktif.{' '}
                      {isAdmin
                        ? 'Buat event baru di bawah.'
                        : 'Minta owner untuk membuat event baru.'}
                    </div>
                  )}
                  {(activeEvents ?? []).map((evt) => (
                    <EventChoice
                      key={evt.id}
                      event={evt}
                      selected={selectedEventId === evt.id}
                      onClick={() => setSelectedEventId(evt.id)}
                    />
                  ))}
                </div>
                {isAdmin && (
                  <div className="mt-3 pt-3 border-t border-line">
                    <Button
                      variant="ghost"
                      size="md"
                      fullWidth
                      leftIcon={<Plus size={16} />}
                      onClick={() => setShowNewEventForm(true)}
                    >
                      Buat event baru
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <input
                  type="text"
                  value={newEventName}
                  onChange={(e) => setNewEventName(e.target.value)}
                  placeholder="Nama event, mis. Pameran Medis Surabaya 2026"
                  className="w-full px-3 py-2.5 rounded border border-line bg-surface text-body text-ink focus:outline-none focus:border-primary"
                />
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={() => {
                      setShowNewEventForm(false);
                      setNewEventName('');
                    }}
                  >
                    Batal
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleCreateAndStart}
                    loading={busy || createEvent.isPending}
                    disabled={!newEventName.trim()}
                  >
                    Buat & mulai shift
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Start button */}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            rightIcon={<ChevronRight size={20} />}
            onClick={handleStart}
            disabled={!selectedEventId || showNewEventForm}
            className="h-16 text-lg"
          >
            Mulai shift POS
          </Button>
        </div>
      </main>
    </div>
  );
}

function EventChoice({
  event,
  selected,
  onClick,
}: {
  event: AppEvent;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded border text-left transition-colors ${
        selected
          ? 'border-primary bg-primary/5'
          : 'border-line bg-canvas hover:border-primary/40'
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-body font-medium text-ink truncate">{event.name}</p>
        <p className="text-caption text-ink-subtle font-mono">{event.code}</p>
        <p className="text-caption text-ink-subtle mt-0.5">
          {formatDateTime(event.startAt)} · {formatRelative(event.startAt)}
        </p>
      </div>
      <ChevronRight size={16} className="text-ink-subtle shrink-0" />
    </button>
  );
}
